'use client';

import { useEffect, useState, useMemo } from 'react';
import { useNinetailed } from '@ninetailed/experience.js-next';
import Disclosure from './Disclosure';
import { DisclosureProps } from 'libs/contentful/props';
import { useDisclosureContext, useConsumedDisclosures, useDisclosureNumberingMap } from 'contexts/DisclosureProvider';
import { AnalyticsModuleTrackingProvider } from 'components/analytics/AnalyticsModuleTracking';
import { VariantDisclosureSet } from 'utils/disclosures';

interface DynamicDisclosuresProps {
  baseline?: DisclosureProps[];
  variants?: VariantDisclosureSet[];
  ninetailedMode?: 'server' | 'client';
}

/**
 * Renders page disclosures that can swap based on active Ninetailed variants (if experiments exist).
 *
 * Without experiments: Simply renders the page's disclosures
 *
 * With experiments:
 * - Server mode: Shows the server-selected variant's disclosures (static)
 * - Client mode: Monitors Ninetailed and dynamically swaps to the correct variant's disclosures
 */
const DynamicDisclosures = ({ baseline = [], variants = [], ninetailedMode = 'server' }: DynamicDisclosuresProps) => {
  const [activeDisclosures, setActiveDisclosures] = useState(baseline);
  const { registerDisclosures } = useDisclosureContext();
  const { consumedDisclosureIds } = useConsumedDisclosures();
  const disclosureNumberingMap = useDisclosureNumberingMap();
  const ninetailed = useNinetailed();

  useEffect(() => {
    // Register active disclosures with the provider whenever they change
    registerDisclosures({
      moduleId: 'variant-aware-disclosures',
      disclosures: activeDisclosures,
      source: 'ninetailed-variant',
    });
  }, [activeDisclosures, registerDisclosures]);

  useEffect(() => {
    // In server mode, skip client-side variant detection and use initial prop value
    if (ninetailedMode !== 'client') {
      return;
    }

    // If no variants to swap, just use baseline
    if (variants.length === 0) {
      setActiveDisclosures(baseline);
      return;
    }

    if (!ninetailed?.onSelectVariant) {
      // Ninetailed not loaded yet, use baseline
      setActiveDisclosures(baseline);
      return;
    }

    const detectActiveVariant = () => {
      const windowNinetailed = (window as unknown as Record<string, unknown>).ninetailed as
        | Record<string, unknown>
        | undefined;

      // Get active variant from Ninetailed's preview plugin
      let previewOverrides: Record<string, number> | null = null;

      if (windowNinetailed) {
        const plugins = windowNinetailed.plugins as Record<string, unknown> | undefined;
        const preview = plugins?.preview as Record<string, unknown> | undefined;
        previewOverrides = (preview?.experienceVariantIndexes as Record<string, number>) || null;
      }

      // If we have preview overrides, use them; otherwise use baseline
      if (!previewOverrides) {
        setActiveDisclosures(baseline);
        return;
      }

      // Find matching variant based on preview overrides
      for (const variantSet of variants) {
        const selectedVariant = previewOverrides[variantSet.experienceId];

        if (selectedVariant !== undefined && selectedVariant === variantSet.variantIndex) {
          setActiveDisclosures(variantSet.disclosures);
          return;
        }
      }

      // Use baseline when no match is found
      setActiveDisclosures(baseline);
    };

    // Initial detection
    detectActiveVariant();

    // Subscribe to variant changes using Ninetailed's onSelectVariant API
    const unsubscribe = ninetailed.onSelectVariant(
      { baseline: { id: '' }, experiences: [] } as unknown as Parameters<typeof ninetailed.onSelectVariant>[0],
      () => {
        // When variant changes, detect the active variant from preview plugin
        detectActiveVariant();
      },
    );

    // Cleanup subscription on unmount
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [baseline, variants, ninetailedMode, ninetailed]);

  // Filter out disclosures that have been consumed by DisclosureAddOn modules
  const disclosures = useMemo(() => {
    return activeDisclosures.filter(disclosure => {
      const disclosureId = disclosure.sys?.id;
      if (!disclosureId) return true; // Keep disclosures without IDs
      return !consumedDisclosureIds.has(disclosureId);
    });
  }, [activeDisclosures, consumedDisclosureIds]);

  if (!disclosures.length) return null;

  return (
    <AnalyticsModuleTrackingProvider
      initialAttributes={{
        moduleType: 'dynamic-disclosures',
        moduleTitle: 'Dynamic Disclosures',
        moduleIndex: '1',
        pageTitle: typeof document !== 'undefined' ? document.title : '',
      }}>
      <div className="mb-2 last:mb-0 dynamic-disclosures">
        <div className="content-wrapper">
          <div className="flex flex-col gap-y-2">
            {disclosures.map((disclosure, index) => {
              // Get auto number from the global numbering map to maintain consistency
              const disclosureId = disclosure.sys?.id;
              const autoNumber = !disclosure.symbol && disclosureId
                ? disclosureNumberingMap.get(disclosureId)
                : undefined;

              return <Disclosure key={disclosureId || index} {...disclosure} autoNumber={autoNumber} />;
            })}
          </div>
        </div>
      </div>
    </AnalyticsModuleTrackingProvider>
  );
};

export default DynamicDisclosures;
